import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout";
import { SidebarProvider } from "@/context/SidebarContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "./components/ui/toaster";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useErrorHandler } from "@/hooks/useErrorHandler";

// Pages
import Home from "@/pages/Home";
import PatientDetailPage from "@/pages/PatientDetailPage";
import AppointmentListPage from "@/pages/AppointmentsPage";
import BillingPage from "@/pages/BillingPage";
import PrescriptionListPage from "@/pages/PrescriptionsPage";
import EditPrescriptionPage from "@/pages/EditPrescriptionPage";
import SettingsPage from "@/pages/SettingsPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SignUpPage from "@/pages/SignUpPage";
import TemplateManagementPage from "./pages/TemplateManagementPage";
import PatientManagementPage from "./pages/PatientManagementPage";
import NotFoundPage from "./pages/NotFoundPage";
import DebugLogs from "./components/DebugLogs";

// Receptionist Pages
import ReceptionistDashboard from "@/pages/frontdesk/FrontDeskDashboard";
import QueueManagement from "@/pages/frontdesk/QueueManagement";
import FrontDeskPatientPage from "@/pages/FrontDeskPatientPage";

function AppContent() {
  useErrorHandler({
    showToast: true,
    logToConsole: true,
    logToService: process.env.NODE_ENV === 'production'
  });

  return (
      <Router>
        <Routes>
          {/* Public Routes - No Layout */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<SignUpPage />} />

          {/* Protected Routes - With MainLayout */}
          <Route element={<MainLayout />}>
            <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Home />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/patients"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "billing"]}>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/patients/:id"
                element={
                  <ProtectedRoute allowedRoles={["doctor"]}>
                    <ErrorBoundary>
                      <PatientDetailPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/appointments"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "staff"]}>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/prescriptions"
                element={
                  <ProtectedRoute allowedRoles={["doctor"]}>
                    <ErrorBoundary>
                      <PrescriptionListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/prescriptions/edit/:id"
                element={
                  <ProtectedRoute allowedRoles={["doctor"]}>
                    <ErrorBoundary>
                      <EditPrescriptionPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/library"
                element={
                  <Navigate to="/lab-tests" replace />
                }
            />
            <Route
                path="/billing"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <BillingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/templates"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "staff"]}>
                    <ErrorBoundary>
                      <TemplateManagementPage/>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["admin", "doctor", "staff"]}>
                    <ErrorBoundary>
                      <SettingsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/debug"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <ErrorBoundary>
                      <DebugLogs />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />

            {/* Front Desk Routes */}
            <Route
                path="/frontdesk"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <ReceptionistDashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/queue"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <QueueManagement />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/patients"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/patients/new"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/patients/:patientId"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <FrontDeskPatientPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/appointments"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/appointments/new"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/billing"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <BillingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
          </Route>

          {/* 404 - Outside MainLayout, No Sidebar/Header */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster />
      </Router>
  );
}

export default function App() {
  return (
      <ErrorBoundary
          onError={(error, errorInfo) => {
            console.error('Global app error:', error, errorInfo);

            if (process.env.NODE_ENV === 'production') {
              // Send to monitoring service
            }
          }}
      >
        <ThemeProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </ThemeProvider>
      </ErrorBoundary>
  );
}